import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { VideoSummary } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchTags, type TagWithCount } from '../queries/tags';
import { fetchLibrary, libraryScopeKey, type LibraryQuery } from '../queries/videos';
import { VideoCard } from './VideoCard';
import { TagScopeLabel } from './TagScope';
import { EmptyState, ErrorState, cx } from './ui';
import { errorMessage } from '../lib/api';

/**
 * The library organized by tag: one shelf per tag, its name over a row of
 * cards that scrolls sideways, and See all for the whole of it. The shelves
 * stack down the page, and every filter above them (search, date, whose,
 * sort, other tags) narrows each one the same way it narrows the grid.
 *
 * A shelf asks for its recordings only when it nears the screen, so a
 * person with forty tags is not forty requests on arrival. One the filters
 * leave empty is dropped rather than shown bare.
 */

const SHELF_SIZE = 12;

export function TagShelves({
  query,
  onSeeAll,
  onTag,
  onDelete,
  canDelete,
  deletingId,
}: {
  /** The library query the filters make; each shelf adds its own tag. */
  query: LibraryQuery;
  onSeeAll: (tag: TagWithCount) => void;
  onTag?: (tagId: string) => void;
  onDelete?: (video: VideoSummary) => void;
  canDelete?: (video: VideoSummary) => boolean;
  deletingId?: string | null;
}) {
  const tags = useQuery({ queryKey: qk.tags(), queryFn: () => fetchTags(), staleTime: 15_000 });
  const filterTags = query.tags ?? [];
  // A tag already in the filter would be a shelf of everything shown; leave it out.
  const shelves = (tags.data?.tags ?? []).filter((tag) => !filterTags.includes(tag.id));

  if (tags.isPending) return <ShelfSkeleton />;
  if (tags.isError) {
    return <ErrorState title="Your tags did not load" message={errorMessage(tags.error)} onRetry={() => void tags.refetch()} />;
  }
  if (shelves.length === 0) {
    return (
      <EmptyState title="No tags to organize by">
        Open a recording and add a tag from its Tags tab. Every tag gets a shelf here with the
        recordings that carry it.
      </EmptyState>
    );
  }

  return (
    <div className="space-y-10">
      {shelves.map((tag) => (
        <TagShelf
          key={tag.id}
          tag={tag}
          query={{ ...query, tags: [...filterTags, tag.id], limit: SHELF_SIZE, cursor: null }}
          onSeeAll={() => onSeeAll(tag)}
          onTag={onTag}
          onDelete={onDelete}
          canDelete={canDelete}
          deletingId={deletingId}
        />
      ))}
    </div>
  );
}

function TagShelf({
  tag,
  query,
  onSeeAll,
  onTag,
  onDelete,
  canDelete,
  deletingId,
}: {
  tag: TagWithCount;
  query: LibraryQuery;
  onSeeAll: () => void;
  onTag?: (tagId: string) => void;
  onDelete?: (video: VideoSummary) => void;
  canDelete?: (video: VideoSummary) => boolean;
  deletingId?: string | null;
}) {
  const root = useRef<HTMLElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const node = root.current;
    if (!node || near) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true);
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near]);

  const shelf = useQuery({
    queryKey: qk.videos(libraryScopeKey(query) + `:shelf${SHELF_SIZE}`),
    queryFn: () => fetchLibrary(query),
    enabled: near,
    staleTime: 20_000,
    placeholderData: (previous) => previous,
  });
  const videos = shelf.data?.videos ?? [];
  const total = shelf.data?.total ?? videos.length;

  // The filters left nothing under this tag.
  if (shelf.isSuccess && videos.length === 0) return null;

  return (
    <section ref={root} aria-labelledby={`shelf-${tag.id}`}>
      <div className="mb-3 flex items-baseline gap-3">
        <h2 id={`shelf-${tag.id}`} className="truncate text-h3 font-semibold text-chalk">
          {tag.name}
        </h2>
        <span className="flex shrink-0 items-center gap-1.5 text-caption text-chalk-faint">
          <TagScopeLabel scope={tag.scope} />
          {shelf.isSuccess ? <span className="tnum">· {total} {total === 1 ? 'video' : 'videos'}</span> : null}
        </span>
        <button
          type="button"
          onClick={onSeeAll}
          className="ml-auto shrink-0 text-caption font-medium text-signal hover:underline"
        >
          See all
        </button>
      </div>

      {shelf.isError ? (
        <p className="text-caption text-chalk-faint">
          This shelf did not load. {errorMessage(shelf.error)}{' '}
          <button type="button" onClick={() => void shelf.refetch()} className="text-signal hover:underline">
            Try again
          </button>
        </p>
      ) : (
        <div
          className={cx(
            'flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3',
            shelf.isPlaceholderData && 'opacity-60 transition-opacity',
          )}
        >
          {shelf.isSuccess
            ? videos.map((video) => (
                <div key={video.id} className="w-[18rem] shrink-0 snap-start">
                  <VideoCard
                    video={video}
                    onTag={onTag}
                    onDelete={onDelete && (!canDelete || canDelete(video)) ? onDelete : undefined}
                    deleting={deletingId === video.id}
                  />
                </div>
              ))
            : Array.from({ length: 4 }, (_, index) => <CardSkeleton key={index} />)}
          {shelf.isSuccess && total > videos.length ? (
            <button
              type="button"
              onClick={onSeeAll}
              className="card flex w-[10rem] shrink-0 snap-start items-center justify-center text-body text-chalk-dim transition-colors duration-120 hover:border-slate-600 hover:text-chalk"
            >
              See all {total}
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}

function CardSkeleton() {
  return (
    <div className="card w-[18rem] shrink-0 overflow-hidden">
      <div className="shimmer aspect-video w-full" />
      <div className="space-y-2 p-3.5">
        <div className="shimmer h-3.5 w-3/4 rounded-xs" />
        <div className="shimmer h-3 w-1/3 rounded-xs" />
      </div>
    </div>
  );
}

function ShelfSkeleton() {
  return (
    <div className="space-y-10">
      {Array.from({ length: 2 }, (_, row) => (
        <div key={row}>
          <div className="shimmer mb-3 h-5 w-32 rounded-xs" />
          <div className="flex gap-4 overflow-hidden">
            {Array.from({ length: 4 }, (_, index) => (
              <CardSkeleton key={index} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
